import {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  PropsWithChildren,
  FC,
} from 'react';
import { 
  Drawer, 
  Box, 
  Typography, 
  Button, 
  Stack,
  Avatar,
  DialogContent,
} from '@mui/material';
import { IosShare, GetAppOutlined } from '@mui/icons-material';

import config from '@shared/config';
import {
  isMobile,
  isAndroid,
  isFirefox,
  isIOS,
  isOpera,
  browserVersion,
  isInAppBrowser,
} from '@shared/helpers/device';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// --- PLATFORM Logic ---

const platforms = {
  NATIVE: 'native', // Chrome, Edge mobile, Samsung browser
  FIREFOX: 'firefox',
  FIREFOX_NEW: 'firefox_new', // Firefox >= 79
  OPERA: 'opera',
  IDEVICE: 'idevice',
  DESKTOP: 'desktop', // Desktop browsers
  OTHER: 'other',
};

function getPlatform() {
  if (!isMobile) return platforms.DESKTOP;
  if (window.hasOwnProperty('BeforeInstallPromptEvent')) return platforms.NATIVE;
  if (isAndroid && isFirefox && +browserVersion >= 79) return platforms.FIREFOX_NEW;
  if (isAndroid && isFirefox) return platforms.FIREFOX;
  if (isOpera && isAndroid) return platforms.OPERA;
  if (isIOS) return platforms.IDEVICE;
  return platforms.OTHER;
}

const ReactPWAInstallContext = createContext({
  supported: false,
  isInstalled: false,
  pwaInstall: () => {},
});

export const useReactPWAInstall = () => useContext(ReactPWAInstallContext);

export const InstallPrompt: FC<PropsWithChildren> = ({ children }) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const platform = useMemo(getPlatform, []); // Run once at mount

  const logger = useCallback((msg: string, err?: Error) =>
    (err ? console.error('[PWA-Install]', msg, err) : console.log('[PWA-Install]', msg)), []);

  const isInstalled = useCallback(() => {
    if (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches) {
      logger('Already running in standalone mode.');
      return true;
    }
    return false;
  }, [logger]);

  // Supported only on a set of platforms
  const supported = useCallback(() => {
    if (deferredPromptRef.current && (platform === platforms.NATIVE || platform === platforms.DESKTOP)) return true;
    if (platform !== platforms.NATIVE && platform !== platforms.DESKTOP && platform !== platforms.OTHER) return true;
    return false;
  }, [platform]);

  // Capture beforeinstallprompt event
  useEffect(() => {
    function onBeforeInstallPrompt(e: any) {
      // Only prevent default for mobile platforms where we want to show our custom UI
      if (platform !== platforms.DESKTOP) {
        e.preventDefault();
      }
      
      deferredPromptRef.current = e;
      logger('beforeinstallprompt event fired');
      
      // If not yet installed and supported, show drawer (only on mobile)
      if (!isInstalled() && supported() && platform !== platforms.DESKTOP) {
        setShowDrawer(true);
      }
    }
    
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () =>
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, [supported, isInstalled, platform, logger]);

  // On mount, also check if we need to show the drawer
  useEffect(() => {
    if (!isInstalled() && supported() && platform !== platforms.DESKTOP) {
      setShowDrawer(true);
    }
  }, [isInstalled, supported, platform]);

  // Perform install: prompt right away and close drawer
  const pwaInstall = useCallback(() => {
    const promptEvt = deferredPromptRef.current;
    if (promptEvt) {
      promptEvt
        .prompt()
        .then(() => promptEvt.userChoice)
        .then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            logger('User accepted the install prompt');
          } else {
            logger('User dismissed the install prompt');
          }
          setShowDrawer(false);
        })
        .catch((err) => {
          logger('Error when triggering prompt', err);
          setShowDrawer(false);
        });
    } else {
      logger('No deferredPrompt event available: cannot show install prompt');
      setShowDrawer(false);
    }
  }, [logger]);

  // -- Context value --
  const ctxValue = useMemo(
    () => ({
      supported: supported(),
      isInstalled: isInstalled(),
      pwaInstall,
    }),
    [supported, isInstalled, pwaInstall],
  );

  const handleClose = () => {
    setShowDrawer(false);
  };

  const showInstallDrawer = !isInAppBrowser && !isInstalled() && supported() && showDrawer && platform !== platforms.DESKTOP;

  return (
    <ReactPWAInstallContext.Provider value={ctxValue}>
      {children}
      <Drawer
        anchor="bottom"
        open={showInstallDrawer}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 1,
              m: 2,
              pb: 'env(safe-area-inset-bottom)',
            },
          },
        }}
      >
        <DialogContent>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar 
              src="/logo.png" 
              sx={{ 
                width: 72, 
                height: 72, 
                p: 1,
                mb: 2,
                borderColor: '#000',
                borderStyle: 'solid',
                borderWidth: 2,
                boxShadow: '-5px 5px 0 #000',
              }}
              variant="square"
            >
              {config.projectName.charAt(0)}
            </Avatar>

            <Typography variant="h1" component="h2" align="center" fontWeight={600} gutterBottom>
              Install {config.projectName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" align="center">
              {platform === platforms.IDEVICE
                ? 'Add this app to your home screen for a better experience'
                : 'Install this app on your device for quick and easy access'
              }
            </Typography>
          </Box>
          
          <Stack direction="column" spacing={2}>
            {platform === platforms.IDEVICE ? (
              <Box sx={{ px: 2 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  To install {config.projectName}:
                </Typography>
                
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>1.</Box>
                    <Typography variant="body2">
                      Tap the share button <IosShare fontSize="small" sx={{ verticalAlign: 'text-bottom' }} />
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>2.</Box>
                    <Typography variant="body2">
                      Scroll down and tap <strong>Add to Home Screen</strong>
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={pwaInstall}
                startIcon={<GetAppOutlined />}
              >
                Install {config.projectName}
              </Button>
            )}
            <Button
              variant="text"
              fullWidth
              onClick={handleClose}
              color="secondary"
              sx={{ mt: 1 }}
            >
              Maybe Later
            </Button>
          </Stack>
        </DialogContent>
      </Drawer>
    </ReactPWAInstallContext.Provider>
  );
};
